import { useEffect, useRef, useState } from "react";
import { Mic, Square, Trash2, Loader2, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";
import { uploadMedia, deleteMedia } from "@/lib/profile/storage";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";
import { useSignedUrl } from "@/lib/media/useSignedUrl";

const MAX_SECONDS = 30;

interface Props {
  value: string | null;
  onChange: (url: string | null) => void;
}

const VoiceRecorder = ({ value, onChange }: Props) => {
  const { user } = useAuth();
  const signedValue = useSignedUrl(value);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const start = async () => {
    if (!user) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setUploading(true);
        try {
          if (value) await deleteMedia("voice-intros", value);
          const url = await uploadMedia("voice-intros", user.id, blob, "voice", "webm");
          onChange(url);
          toast.success("Voice intro saved.");
        } catch (e) {
          toast.error((e as Error).message || "Upload failed.");
        } finally {
          setUploading(false);
        }
      };
      recorderRef.current = mr;
      mr.start();
      setRecording(true);
      setSeconds(0);
      timerRef.current = window.setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_SECONDS) {
            stop();
            return MAX_SECONDS;
          }
          return s + 1;
        });
      }, 1000);
    } catch {
      toast.error("Microphone access denied.");
    }
  };

  const stop = () => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    recorderRef.current?.stop();
    setRecording(false);
  };

  const remove = async () => {
    if (!value) return;
    await deleteMedia("voice-intros", value);
    onChange(null);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else void audioRef.current.play();
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Voice intro</p>
          <p className="text-xs text-muted-foreground">Up to {MAX_SECONDS} seconds.</p>
        </div>
        {recording && (
          <span className="text-xs font-mono text-destructive">
            ● {seconds}s / {MAX_SECONDS}s
          </span>
        )}
      </div>

      {value && !recording && (
        <div className="flex items-center gap-3 rounded-lg bg-secondary px-3 py-2">
          <Button type="button" size="icon" variant="ghost" onClick={togglePlay}>
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <audio
            ref={audioRef}
            src={signedValue ?? undefined}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
            className="flex-1"
            controls
          />
        </div>
      )}

      <div className="flex gap-2">
        {!recording ? (
          <Button type="button" variant="outline" onClick={start} disabled={uploading}>
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
            {value ? "Re-record" : "Record"}
          </Button>
        ) : (
          <Button type="button" variant="destructive" onClick={stop}>
            <Square className="h-4 w-4" />
            Stop
          </Button>
        )}
        {value && !recording && (
          <Button type="button" variant="ghost" onClick={remove}>
            <Trash2 className="h-4 w-4" />
            Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default VoiceRecorder;
